import { useMemo } from "react";

import { Prerequisite } from "@/v2/entities/prerequisite/Prerequisite.model";
import { Instruction } from "@/v2/entities/instruction/Instruction.model";
import { Topic } from "@/v2/entities/topic/Topic.model";
import { TopicReleasePolicy } from "@/v2/entities/topic-release-policy/TopicReleasePolicy.model";

import { useSimulator } from "./SimulatorWrapper";
import { TopicChange, useDraft } from "./useDraft";

interface TopicDraft {
  topic: Topic | null;
  brief: string | null;
  isKnowledgeEnabled?: boolean;
  isHistoricalTicketsEnabled?: boolean;
  isWikiEnabled?: boolean;
  escalationStrategy?: string;
  overrideStrictMode?: boolean;
  overrideMaxBackAndForth?: number;
  topicReleasePolicy?: TopicReleasePolicy;
  instructions: Instruction[];
  prerequisites: Prerequisite[];
  draftDetails: TopicChange | null;
}

export const useTopicDraft = ({ topicId }: { topicId: number }): TopicDraft => {
  const { topics, currentRelease } = useSimulator();
  const { topics: topicDraft } = useDraft();

  return useMemo(() => {
    const topic = topics?.find((t) => t.id === topicId);

    const topicReleasePolicy = currentRelease?.topicReleasePolicies?.find(
      (policy) => policy.topicId === topicId
    );

    const draftDetails = topicDraft.find((t) => t.topicId === topicId);

    return {
      topic: topic || null,
      brief: topicReleasePolicy?.brief || null,
      isKnowledgeEnabled: topicReleasePolicy?.isKnowledgeEnabled ?? true,
      isHistoricalTicketsEnabled:
        topicReleasePolicy?.isHistoricalTicketsEnabled ?? true,

      isWikiEnabled: topicReleasePolicy?.isWikiEnabled ?? false,
      escalationStrategy: topicReleasePolicy?.escalationStrategy,
      overrideStrictMode: topicReleasePolicy?.overrideStrictMode ?? undefined,
      overrideMaxBackAndForth:
        topicReleasePolicy?.overrideMaxBackAndForth ?? undefined,
      instructions: topicReleasePolicy?.instructions || [],
      prerequisites: topicReleasePolicy?.prerequisites || [],
      topicReleasePolicy,
      draftDetails: draftDetails || null,
    };
  }, [topicId, topics, currentRelease, topicDraft]);
};
